<script setup>
import { computed } from 'vue'
import moment from 'moment'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import { getInviteTimestamp, resolveTeamMember } from '@/Utils/teamAccessSummary'

const props = defineProps({
	params: {
		type: Object,
		default: null,
	},
	member: {
		type: Object,
		default: null,
	},
	// Compact rendering for the card meta line: a bare dot + word (no pill
	// background, no stacked subtitle). The desktop STATUS column keeps the
	// fuller pill (dot = false).
	dot: {
		type: Boolean,
		default: false,
	},
})

const data = computed(() => resolveTeamMember(props))

const status = computed(() => {
	// On the roster, but never granted the workspace the page stands in.
	if (data.value.onProject === false && data.value.status !== 'pending') {
		return { variant: 'muted', label: 'Not added', subtitle: null }
	}
	if (data.value.status === 'pending' && data.value.onProject === false) {
		return { variant: 'muted', label: 'Invited elsewhere', subtitle: null }
	}
	if (data.value.status === 'pending') {
		const invitedAt = getInviteTimestamp(data.value)
		const relative = invitedAt ? moment(invitedAt).fromNow() : null
		return {
			variant: 'warning',
			label: 'Invited',
			relative,
			// Only pending keeps a subtitle — "Invited N days ago".
			subtitle: relative ? `Invited ${relative}` : 'Invitation sent',
		}
	}
	if (data.value.enabled === false) {
		return { variant: 'muted', label: 'Paused', subtitle: null }
	}
	return { variant: 'success', label: 'Active', subtitle: null }
})

// The compact dot line always leads with the status word. For pending it folds
// the relative timing inline ("Invited 3 days ago") when we know it.
const dotText = computed(() => {
	if (data.value.status === 'pending' && data.value.onProject !== false) {
		return status.value.relative ? `Invited ${status.value.relative}` : 'Invited'
	}
	return status.value.label
})
</script>

<template>
	<StatusPill
		v-if="dot"
		:variant="status.variant"
		size="sm"
		class="leading-none"
	>{{ dotText }}</StatusPill>
	<div v-else class="flex flex-col justify-center items-start gap-1 min-w-0 h-full">
		<StatusPill :variant="status.variant" pill size="sm" class="w-fit shrink-0 leading-none">{{ status.label }}</StatusPill>
		<span v-if="status.subtitle" class="text-tiny text-surface-6 leading-none">{{ status.subtitle }}</span>
	</div>
</template>
